import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { api, ApiError } from './api'
import type { AppMeta, Lookups, Me } from './types'

export const sessionKey = ['session'] as const

async function fetchSession(): Promise<Me | null> {
  try {
    return (await api<{ user: Me | null }>('/auth/session/')).user
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return null
    throw error
  }
}

/** The signed-in user, or `null` when signed out. */
export function useSession() {
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: sessionKey, queryFn: fetchSession, staleTime: 5 * 60_000 })

  useEffect(() => {
    // Any 401 from the API means the session ended (expired, signed out elsewhere).
    const onUnauthorized = () => queryClient.setQueryData(sessionKey, null)
    window.addEventListener('pcms:unauthorized', onUnauthorized)
    return () => window.removeEventListener('pcms:unauthorized', onUnauthorized)
  }, [queryClient])

  return query
}

export function useMe(): Me {
  const { data } = useSession()
  if (!data) throw new Error('useMe() used outside an authenticated route')
  return data
}

export function useAppMeta() {
  return useQuery({ queryKey: ['meta'], queryFn: () => api<AppMeta>('/meta/'), staleTime: Infinity })
}

export function useLookups() {
  return useQuery({ queryKey: ['lookups'], queryFn: () => api<Lookups>('/lookups/'), staleTime: 60_000 })
}

export function homePathFor(user: Me | null | undefined): string {
  if (!user) return '/login'
  return user.role === 'student' ? '/portal' : '/staff'
}
